import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, MessageSquare } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { BrandButton } from "@/components/PageHeader";
import { useCaptcha, CaptchaField } from "@/components/auth/Captcha";
import { api, errMsg } from "@/lib/api";

const EMPTY = { name: "", email: "", phone: "", subject: "", message: "" };

const ContactForm = ({ busy, setBusy, onSent }) => {
  const [f, setF] = useState(EMPTY);
  const [error, setError] = useState("");
  const errorRef = useRef(null);
  const captcha = useCaptcha("math");
  useEffect(() => { if (error) errorRef.current?.focus(); }, [error]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const valid = f.name.trim().length >= 2 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim()) && f.message.trim().length >= 5 && captcha.answer.trim();
  const send = async (e) => {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true); setError("");
    try {
      await api.post("/contact", { ...f, ...captcha.fields });
      toast.success("Thank you! Your message has been sent.");
      onSent();
    } catch (err) {
      setError(errMsg(err));
      toast.error(errMsg(err));
      captcha.reload();
    }
    setBusy(false);
  };
  return (
    <form onSubmit={send} className="grid grid-cols-2 gap-3" data-testid="contact-form">
      <Input disabled={busy} value={f.name} onChange={set("name")} placeholder="Full name *" className="col-span-2 sm:col-span-1" data-testid="contact-name-input" />
      <Input disabled={busy} value={f.email} onChange={set("email")} placeholder="Email *" type="email" className="col-span-2 sm:col-span-1" data-testid="contact-email-input" />
      <Input disabled={busy} value={f.phone} onChange={set("phone")} placeholder="Phone (optional)" className="col-span-2 sm:col-span-1" data-testid="contact-phone-input" />
      <Input disabled={busy} value={f.subject} onChange={set("subject")} placeholder="Subject" className="col-span-2 sm:col-span-1" data-testid="contact-subject-input" />
      <Textarea disabled={busy} value={f.message} onChange={set("message")} rows={5} placeholder="Your message *" className="col-span-2" data-testid="contact-message-input" />
      <div className="col-span-2"><CaptchaField c={captcha} testId="contact-captcha" /></div>
      <p id="contact-required-hint" className="col-span-2 text-xs text-slate-600" data-testid="contact-required-hint">Enter your full name, a valid email, a message of at least 5 characters and the security check answer to enable Send message.</p>
      {error && <p ref={errorRef} tabIndex={-1} role="alert" className="col-span-2 rounded-md border border-red-200 bg-red-50 text-red-900 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" data-testid="contact-send-error">{error} Your message is still here; you can try again.</p>}
      <BrandButton type="submit" aria-describedby="contact-required-hint" className="col-span-2 h-11" disabled={!valid || busy} data-testid="contact-submit-button"><Send className="w-4 h-4" />{busy ? "Sending…" : "Send message"}</BrandButton>
    </form>
  );
};

export const ContactDialog = () => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) { setOpen(v); } }}>
      <DialogTrigger asChild>
        <button className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-[#E60000] hover:bg-[#CC0000] text-white text-sm font-semibold transition-colors" data-testid="footer-contact-us-button">
          <MessageSquare className="w-4 h-4" />Contact us
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="contact-dialog">
        <DialogHeader><DialogTitle className="font-heading">Contact GAMLS</DialogTitle><DialogDescription>Send the secretariat a message. Fields marked * are required.</DialogDescription></DialogHeader>
        <ContactForm busy={busy} setBusy={setBusy} onSent={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  );
};
